import { Datasource_FieldSpec, Datasource_Preset } from '@/types/api';
import { filterNulls } from '@/utils/filterNulls';

/**
 * Get the changes between two field specs. It should return the upgrade versions of the fields that changed.
 * @param currentFields - The current field specs
 * @param upgradeFields - The upgrade field specs
 * @returns The changes between the two field specs
 */
export const getFieldChanges = (
  currentFields: Datasource_FieldSpec[],
  upgradeFields: Datasource_FieldSpec[],
): Datasource_FieldSpec[] => {
  return filterNulls(
    upgradeFields.map((upgradeField) => {
      const currentField = currentFields.find(
        ({ name }) => name === upgradeField.name,
      );

      // If the field is not in the current preset, return the upgrade field since this is a new field.
      if (!currentField) return upgradeField;

      // If the field is unchanged, return undefined.
      if (currentField.expr && currentField.expr === upgradeField.expr) {
        return undefined;
      }

      // If the field is unchanged, return undefined.
      if (currentField.alias && currentField.alias === upgradeField.alias) {
        return undefined;
      }

      // If the field is unchanged, return undefined.
      if (currentField.from && currentField.from === upgradeField.from) {
        return undefined;
      }

      // If the field is unchanged, return undefined.
      if (
        currentField.literal &&
        currentField.literal === upgradeField.literal
      ) {
        return undefined;
      }

      // The field must be changed, so return the upgrade field.
      return upgradeField;
    }),
  );
};

/**
 * Get the changes between two presets. It should return the upgrade versions of the fields that changed.
 * @param currentPresetValue - The current preset value
 * @param upgradePresetValue - The upgrade preset value
 * @returns The changes between the two presets
 */
export const getPresetChanges = (
  currentPresetValue: Datasource_Preset,
  upgradePresetValue: Datasource_Preset,
): Datasource_Preset => {
  return {
    autoloader: {
      format:
        currentPresetValue.autoloader?.format !==
        upgradePresetValue.autoloader?.format
          ? upgradePresetValue.autoloader?.format || ''
          : '',
      schemaFile:
        currentPresetValue.autoloader?.schemaFile !==
        upgradePresetValue.autoloader?.schemaFile
          ? upgradePresetValue.autoloader?.schemaFile
          : undefined,
      cloudFiles: {
        schemaHints:
          currentPresetValue.autoloader?.cloudFiles?.schemaHints !==
          upgradePresetValue.autoloader?.cloudFiles?.schemaHints
            ? upgradePresetValue.autoloader?.cloudFiles?.schemaHints
            : undefined,
        schemaHintsFile:
          currentPresetValue.autoloader?.cloudFiles?.schemaHintsFile !==
          upgradePresetValue.autoloader?.cloudFiles?.schemaHintsFile
            ? upgradePresetValue.autoloader?.cloudFiles?.schemaHintsFile
            : undefined,
      },
    },
    silver: {
      preTransform: currentPresetValue.silver?.preTransform?.map(
        (current, index) => {
          const upgrade = upgradePresetValue.silver?.preTransform?.[index];
          if (!upgrade || !current) return current;

          return {
            ...current,
            name: upgrade.name,
            filter:
              current.filter !== upgrade.filter ? upgrade.filter : undefined,
            postFilter:
              current.postFilter !== upgrade.postFilter
                ? upgrade.postFilter
                : undefined,
            fields: getFieldChanges(
              filterNulls(current.fields),
              filterNulls(upgrade.fields),
            ),
            utils: {
              ...current.utils,
              temporaryFields:
                JSON.stringify(current.utils?.temporaryFields) !==
                JSON.stringify(upgrade.utils?.temporaryFields)
                  ? upgrade.utils?.temporaryFields
                  : undefined,
              unreferencedColumns: {
                ...current.utils?.unreferencedColumns,
                preserve:
                  current.utils?.unreferencedColumns?.preserve !==
                  upgrade.utils?.unreferencedColumns?.preserve
                    ? upgrade.utils?.unreferencedColumns?.preserve
                    : undefined,
                embedColumn:
                  current.utils?.unreferencedColumns?.embedColumn !==
                  upgrade.utils?.unreferencedColumns?.embedColumn
                    ? upgrade.utils?.unreferencedColumns?.embedColumn
                    : undefined,
                embedColumnType:
                  current.utils?.unreferencedColumns?.embedColumnType !==
                  upgrade.utils?.unreferencedColumns?.embedColumnType
                    ? upgrade.utils?.unreferencedColumns?.embedColumnType
                    : undefined,
                omitColumns:
                  JSON.stringify(
                    current.utils?.unreferencedColumns?.omitColumns,
                  ) !==
                  JSON.stringify(
                    upgrade.utils?.unreferencedColumns?.omitColumns,
                  )
                    ? upgrade.utils?.unreferencedColumns?.omitColumns
                    : undefined,
                duplicatePrefix:
                  current.utils?.unreferencedColumns?.duplicatePrefix !==
                  upgrade.utils?.unreferencedColumns?.duplicatePrefix
                    ? upgrade.utils?.unreferencedColumns?.duplicatePrefix
                    : undefined,
              },
            },
          };
        },
      ),
      transform: upgradePresetValue.silver?.transform?.map((upgrade) => {
        const current = currentPresetValue.silver?.transform?.find(
          (t) => t.name === upgrade.name,
        );
        if (!upgrade || !current) return upgrade;

        return {
          ...current,
          name: upgrade.name,
          filter:
            current.filter !== upgrade.filter ? upgrade.filter : undefined,
          postFilter:
            current.postFilter !== upgrade.postFilter
              ? upgrade.postFilter
              : undefined,
          fields: getFieldChanges(
            filterNulls(current.fields),
            filterNulls(upgrade.fields),
          ),
          utils: {
            ...current.utils,
            temporaryFields:
              JSON.stringify(current.utils?.temporaryFields) !==
              JSON.stringify(upgrade.utils?.temporaryFields)
                ? upgrade.utils?.temporaryFields
                : undefined,
            unreferencedColumns: {
              ...current.utils?.unreferencedColumns,
              preserve:
                current.utils?.unreferencedColumns?.preserve !==
                upgrade.utils?.unreferencedColumns?.preserve
                  ? upgrade.utils?.unreferencedColumns?.preserve
                  : undefined,
              embedColumn:
                current.utils?.unreferencedColumns?.embedColumn !==
                upgrade.utils?.unreferencedColumns?.embedColumn
                  ? upgrade.utils?.unreferencedColumns?.embedColumn
                  : undefined,
              embedColumnType:
                current.utils?.unreferencedColumns?.embedColumnType !==
                upgrade.utils?.unreferencedColumns?.embedColumnType
                  ? upgrade.utils?.unreferencedColumns?.embedColumnType
                  : undefined,
              omitColumns:
                JSON.stringify(
                  current.utils?.unreferencedColumns?.omitColumns,
                ) !==
                JSON.stringify(upgrade.utils?.unreferencedColumns?.omitColumns)
                  ? upgrade.utils?.unreferencedColumns?.omitColumns
                  : undefined,
              duplicatePrefix:
                current.utils?.unreferencedColumns?.duplicatePrefix !==
                upgrade.utils?.unreferencedColumns?.duplicatePrefix
                  ? upgrade.utils?.unreferencedColumns?.duplicatePrefix
                  : undefined,
            },
          },
        };
      }),
    },
    gold: upgradePresetValue.gold?.map((upgrade) => {
      const current = currentPresetValue.gold?.find(
        (t) => t.name === upgrade.name,
      );
      if (!upgrade || !current) return upgrade;

      return {
        ...current,
        name: upgrade.name,
        input: current.input !== upgrade.input ? upgrade.input : '',
        filter: current.filter !== upgrade.filter ? upgrade.filter : undefined,
        postFilter:
          current.postFilter !== upgrade.postFilter
            ? upgrade.postFilter
            : undefined,
        fields: getFieldChanges(
          filterNulls(current.fields),
          filterNulls(upgrade.fields),
        ),
      };
    }),
  };
};
